export { allocateByWeight } from './adjustment-allocation';
export {
  createEmptyPricingAdjustmentsOutput,
  isPricingAdjustmentPercentInRange,
  PRICING_ADJUSTMENT_BLOCK_LABELS,
  PRICING_ADJUSTMENT_SCOPE_LABELS,
  PRICING_AGENCY_COMMISSION_DEFAULT_PERCENT,
  PRICING_AGENCY_COMMISSION_PRESET_PERCENTS,
  PRICING_COMMISSION_SERVICE_CATEGORY_SCOPES,
  PRICING_SURCHARGE_DEFAULT_PERCENT,
  pricingAdjustmentBasesSchema,
  pricingAdjustmentBlockTotalsSchema,
  pricingAdjustmentDistributionSchema,
  pricingAdjustmentDistributionShareSchema,
  pricingAdjustmentKindSchema,
  pricingAdjustmentScopeSchema,
  pricingAdjustmentsInputSchema,
  pricingAdjustmentsOutputSchema,
  pricingAgencyCommissionBlockOutputSchema,
  pricingAgencyCommissionLineInputSchema,
  pricingAgencyCommissionLineOutputSchema,
  pricingAgencyCommissionScopeSchema,
  pricingSurchargeBlockOutputSchema,
  pricingSurchargeLineInputSchema,
  pricingSurchargeLineOutputSchema,
  pricingSurchargeScopeSchema,
  resolveAgencyCommissionDefaultPercent,
  resolveAgencyCommissionPresetPercents,
  resolveSurchargeDefaultPercent,
  resolveSurchargePercentOptions,
} from './adjustments';
export {
  type CategoryMarkupSettings,
  type PricingSetting,
  type PricingSettingCountryValue,
  resolveCategoryMarkupPercent,
  resolveDefaultPricingValue,
} from './category-markup';
export {
  COMMISSION_CATEGORIES,
  type CommissionCategory,
  type CommissionDeals,
  commissionCategoryOfContent,
  commissionDealsSchema,
  commissionRateFor,
  commissionRateForScope,
  compactCommissionDeals,
  DEFAULT_COMMISSION_RATES,
  type OrgAgencyCommissionSetting,
  parseCommissionDeals,
  type ResolvedCommissionRates,
  resolveCommissionRates,
} from './commission-rates';
export {
  convertForDisplay,
  DISPLAY_SOURCE_CURRENCY,
  DISPLAY_TARGET_CURRENCY,
  type DisplayCurrency,
  type DisplayMoney,
  type DisplayMoneyInput,
  isDisplayCurrency,
  isUsableDisplayRate,
  resolveDisplayMoney,
} from './display-currency';
export {
  DYNAMIC_PRICING_ENABLED,
  type DynamicPriceLine,
  type DynamicPriceResult,
  type DynamicPricingParams,
  type DynamicPricingTraveller,
  dynamicPriceFingerprint,
  hasStaticPrice,
  hotelGroupThresholdWarning,
  priceDynamicProduct,
  roomsBooked,
} from './dynamic-pricing';
export {
  type DynamicPricingRuleIssue,
  dynamicPricingRuleIssues,
  seasonLabel,
} from './dynamic-pricing-rules';
export {
  EXPERIENCE_CATEGORIES,
  type ExperienceCategoryLiteral,
  isExperienceCategory,
} from './experience-categories';
export {
  extractDisplayPrice,
  extractTravellerTypePrice,
} from './extract-display-price';
export {
  type ExtractPriceOptions,
  extractPriceTotal,
} from './extract-price-total';
export { getPriceCategory } from './get-price-category';
export { getProductPrice } from './get-product-price';
export { normalizeTravelerType } from './normalize-traveler-type';
export {
  PRODUCT_PRICING_OPTIONS,
  type ProductPricingOption,
  toProductPricingOption,
} from './product-pricing-options';
export {
  applyCostToSinglePrice,
  estimateTransferCost,
  lookupTransportRouteRate,
  lookupTransportVehicleRate,
  normalizePlaceKey,
  normalizeVehicleKey,
  parseTransportRateSheet,
  type TransferProductPricePatch,
  type TransportRateEstimateMatch,
  type TransportRateEstimateQuery,
  type TransportRateEstimateResult,
  type TransportRateLookupRow,
  type TransportRateRow,
  type TransportRateServiceType,
  type TransportRateSheet,
  type TransportRateUnit,
  type TransportRateVehicleMatch,
  type TransportRateVehicleQuery,
  transportRateRowSchema,
  transportRateServiceTypeSchema,
  transportRateSheetSchema,
  transportRateUnitSchema,
} from './transport-rate-sheet';

// Budget-tier thresholds used by `recomputeDefaultBudgetValues`. Mirrors
// legacy `ProductService.DEFAULT_BUDGET_VALUE_THRESHOLDS`. Falls back when
// the org's settings.budgetValueThresholds is missing or malformed.
export const DEFAULT_BUDGET_VALUE_THRESHOLDS = {
  tier1Max: 200,
  tier2Max: 400,
  tier3Max: 800,
} as const;
export type BudgetValueThresholds = typeof DEFAULT_BUDGET_VALUE_THRESHOLDS;

export type {
  PricingLegacyCategoryKey,
  PricingLegacySubcategoryKey,
} from './content-categories';
export type {
  BudgetCategory,
  BudgetCategoryOutput,
  BudgetCategoryTotals,
  BudgetMappedItemAuditRow,
  BudgetMappingReason,
  BudgetOutput,
  BudgetRedistributionAudit,
  BudgetRedistributionStrategy,
  CalculatedMoneyFields,
  CalculatePricing,
  CurrencyCode,
  EditIntentNewRoomPeriod,
  EditIntentPriceComponent,
  EditIntentRoom,
  EditIntentSource,
  EditIntentTarget,
  ExchangeRateRecord,
  ExchangeRateSet,
  ExchangeRateSource,
  GroupFallbackPolicy,
  GroupPriceStructure,
  GroupThresholdInput,
  InclusionDecision,
  InclusionPolicyResult,
  InclusionPolicyScope,
  MissingTravellerBehavior,
  MoneyAmount,
  MoneyPrecision,
  NormalizedItemFlags,
  NormalizedItemInput,
  NormalizedPriceComponent,
  NormalizedPriceStructure,
  OrderedEditIntents,
  ParentChildInclusionMode,
  PersistenceSafeCalculatedFields,
  PersistenceSafeCalculatedItem,
  PersistenceSafeCalculatedStructure,
  PersistenceSafeGroupCalculated,
  PersistenceSafeGroupThresholdCalculated,
  PersistenceSafeLineItemMetadata,
  PersistenceSafeRoomCalculated,
  PersistenceSafeRoomPeriodCalculated,
  PersistenceSafeRoomsCalculated,
  PersistenceSafeSingleCalculated,
  PersistenceSafeTravellerCalculated,
  PersistenceSafeTravellersCalculated,
  PricingAdjustmentBases,
  PricingAdjustmentBlockTotals,
  PricingAdjustmentDistribution,
  PricingAdjustmentDistributionShare,
  PricingAdjustmentKind,
  PricingAdjustmentLineOutput,
  PricingAdjustmentScope,
  PricingAdjustmentsInput,
  PricingAdjustmentsOutput,
  PricingAgencyCommissionLineInput,
  PricingAgencyCommissionLineOutput,
  PricingAgencyCommissionScope,
  PricingContext,
  PricingDiagnostic,
  PricingDiagnosticCategory,
  PricingDiagnosticSeverity,
  PricingDiagnostics,
  PricingEditIntent,
  PricingInclusionPolicies,
  PricingInclusionPolicy,
  PricingInput,
  PricingOutput,
  PricingSource,
  PricingSurchargeLineInput,
  PricingSurchargeLineOutput,
  PricingSurchargeScope,
  PricingTableOutput,
  PricingTableRow,
  PricingTableRowDisplay,
  PricingTableSection,
  PricingTableTotals,
  PricingUnit,
  RoomPriceInput,
  RoomPricePeriod,
  RoomPricingMode,
  RoomPricingSourceMetadata,
  RoomsPriceStructure,
  SinglePriceStructure,
  SinglePriceUnit,
  TravelerType,
  TravellerInput,
  TravellerMatchPolicy,
  TravellerMatchStrategy,
  TravellerPriceInput,
  TravellerQuantity,
  TravellersPriceStructure,
  VatRate,
} from './types';
export {
  type ComputedPricingRow,
  type ComputePricingRowArgs,
  computePricingRow,
} from './compute-row';
export {
  isPricingAccommodationCategory,
  mapPricingContentCategoryToBudgetCategory,
  PRICING_BUDGET_CATEGORIES,
  PRICING_LEGACY_CATEGORY_LABELS,
  PRICING_LEGACY_SUBCATEGORY_LABELS,
  resolvePricingLegacyCategoryKey,
  resolvePricingLegacySubcategoryKey,
  toPricingLegacyCategoryLabel,
  toPricingLegacySubcategoryLabel,
} from './content-categories';
export {
  pricingDiagnosticCategorySchema,
  pricingDiagnosticPathSegmentSchema,
  pricingDiagnosticSchema,
  pricingDiagnosticSeveritySchema,
  pricingDiagnosticsSchema,
} from './diagnostics';
export {
  addRoomIntentSchema,
  addRoomPeriodIntentSchema,
  addTravellerLineIntentSchema,
  deleteRoomIntentSchema,
  deleteRoomPeriodIntentSchema,
  editIntentGroupThresholdSchema,
  editIntentNewRoomPeriodSchema,
  editIntentNightPeriodSchema,
  editIntentPriceComponentSchema,
  editIntentRoomSchema,
  editIntentSourceSchema,
  editIntentTargetSchema,
  orderedEditIntentsSchema,
  pricingEditIntentSchema,
  removeTravellerLineIntentSchema,
  setCommissionIntentSchema,
  setCostIntentSchema,
  setCostVatIntentSchema,
  setCurrencyIntentSchema,
  setFinalPriceIntentSchema,
  setGroupThresholdsIntentSchema,
  setMarkupIntentSchema,
  setNightPeriodIntentSchema,
  setPriceVatIntentSchema,
  setProfitIntentSchema,
  setRoomTypeIdIntentSchema,
  setTravellerQuantityIntentSchema,
  setUnitPriceIntentSchema,
  setUnitsIntentSchema,
} from './edit-intents';
export {
  agencyCommissionFormulaAmount,
  calculatePricing,
  excludeVatFromAmount,
  markupPriceFromCost,
  roundMoneyAmount,
  roundPercentValue,
} from './engine';
export {
  exchangeRateRecordSchema,
  exchangeRateSetSchema,
  exchangeRateSourceSchema,
} from './exchange-rates';
export {
  inclusionDecisionSchema,
  inclusionPolicyResultSchema,
  inclusionPolicyRulesSchema,
  inclusionPolicyScopeSchema,
  parentChildInclusionModeSchema,
  pricingInclusionPoliciesSchema,
  pricingInclusionPolicySchema,
} from './inclusion-policy';
export {
  calculatedMoneyFieldsSchema,
  currencyCodeSchema,
  finiteNumberSchema,
  moneyAmountSchema,
  moneyPrecisionModeSchema,
  moneyPrecisionSchema,
  moneyRoundingModeSchema,
  nonNegativeNumberSchema,
  nullableMoneyAmountSchema,
  percentageValueSchema,
  vatRateSchema,
} from './money';
export {
  budgetCategoryOutputSchema,
  budgetCategorySchema,
  budgetCategoryTotalsSchema,
  budgetMappedItemAuditRowSchema,
  budgetMappingReasonSchema,
  budgetOutputSchema,
  budgetRedistributionAuditSchema,
  budgetRedistributionStrategySchema,
  groupFallbackPolicySchema,
  groupPriceStructureSchema,
  groupThresholdInputSchema,
  missingTravellerBehaviorSchema,
  normalizedDateRangeSchema,
  normalizedItemFlagsSchema,
  normalizedItemInputSchema,
  normalizedPriceComponentSchema,
  normalizedPriceStructureSchema,
  persistenceSafeCalculatedFieldsSchema,
  persistenceSafeCalculatedItemSchema,
  persistenceSafeCalculatedStructureSchema,
  persistenceSafeGroupCalculatedSchema,
  persistenceSafeGroupThresholdCalculatedSchema,
  persistenceSafeLineItemMetadataSchema,
  persistenceSafeRoomCalculatedSchema,
  persistenceSafeRoomPeriodCalculatedSchema,
  persistenceSafeRoomsCalculatedSchema,
  persistenceSafeSingleCalculatedSchema,
  persistenceSafeTravellerCalculatedSchema,
  persistenceSafeTravellersCalculatedSchema,
  pricingContextSchema,
  pricingInputSchema,
  pricingOutputSchema,
  pricingSourceSchema,
  pricingTableOutputSchema,
  pricingTableRowDisplaySchema,
  pricingTableRowSchema,
  pricingTableSectionSchema,
  pricingTableTotalsSchema,
  pricingUnitSchema,
  roomPriceInputSchema,
  roomPricePeriodSchema,
  roomPricingModeSchema,
  roomPricingSourceMetadataSchema,
  roomsPriceStructureSchema,
  singlePriceStructureSchema,
  singlePriceUnitSchema,
  travelerTypeSchema,
  travellerInputSchema,
  travellerMatchPolicySchema,
  travellerMatchStrategySchema,
  travellerPriceInputSchema,
  travellerQuantitySchema,
  travellersPriceStructureSchema,
} from './schemas';
